import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { AxiosResponse } from 'axios';
import type { FC } from 'react';

import { KBApi } from '../api';
import ArticleList from '../components/ArticleList';
import { Box } from '../components/generic/box/box';
import PageHeader from '../components/PageHeader';
import PageLoading from '../components/PageLoading';
import PageNav from '../components/PageNav';
import TopicMenu from '../components/TopicMenu';
import * as menuStyles from '../components/TopicMenu.style';
import { useDelayedFlag } from '../utils/useDelayedFlag';

import type { Crumb } from '../components/Breadcrumbs';
import type { TopicPage } from '../types';

// A topic page: its articles, with the rest of its section listed beside them.
const ViewTopic: FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // The loaded page keeps the view hash it was fetched with. Links and
  // breadcrumbs are built from that hash, not from the current route, so a
  // page waiting for its replacement never mixes the two.
  const [topic, setTopic] = useState<{ viewHash: string; data: TopicPage } | null>(null);

  // Every request is tagged with the route that asked for it. A response for
  // any other route is dropped, so a slow earlier request cannot overwrite the
  // page the reader is looking at, and a view hash change cannot reuse the
  // previous view's data.
  const routeKey = `${params.hash}/${params.categoryId}/${params.id}`;
  const currentRoute = useRef(routeKey);

  // The ref moves on as soon as the route does, before any effect runs. Setting
  // it inside the request would leave a window where a response for the route
  // just left still matches, and would render under the new address.
  useLayoutEffect(() => {
    currentRoute.current = routeKey;
  }, [routeKey]);

  const fetchTopic = useCallback(async () => {
    setIsLoading(true);

    try {
      const viewId = params.hash;
      const { categoryId, id } = params;
      if (!viewId || !categoryId || !id) {
        throw new Error('Topic route is missing the view hash, the category id or the tag id');
      }
      const response: AxiosResponse<TopicPage> = await KBApi.getTopicById(viewId, categoryId, id);
      if (currentRoute.current !== routeKey) {
        return;
      }
      setTopic({ viewHash: viewId, data: response.data });
      setError(null);
    } catch (err) {
      if (currentRoute.current !== routeKey) {
        return;
      }
      // The retained topic belongs to the route we navigated away from, so it
      // has to go. Leaving it on screen would show the reader a page that the
      // address bar no longer describes.
      setTopic(null);
      setError(t('unknown_error'));
    } finally {
      if (currentRoute.current === routeKey) {
        setIsLoading(false);
      }
    }
  }, [routeKey, params.hash, params.categoryId, params.id, t]);

  useEffect(() => {
    fetchTopic();
  }, [fetchTopic]);

  const isSlow = useDelayedFlag(isLoading);

  if (error) {
    return <button onClick={fetchTopic}>{t('try_again')}</button>;
  }

  if (!topic) {
    return isSlow ? <PageLoading /> : null;
  }

  const { viewHash, data } = topic;
  const crumbs: Crumb[] = [
    { label: t('breadcrumbs.home'), to: `/v/${viewHash}` },
    { label: data.section.name, to: `/v/${viewHash}/c/${data.section.id}` },
    { label: data.name }
  ];

  // A section with a single topic gains nothing from a menu.
  const showMenu = data.siblings.length > 1;

  const content = (
    <>
      <PageHeader title={data.name} description={data.description} icon={data.icon} />
      <ArticleList viewHash={viewHash} articles={data.articles} />
    </>
  );

  return (
    <div aria-busy={isLoading} style={{ opacity: isSlow ? 0.6 : 1, transition: 'opacity 150ms' }}>
      <PageNav crumbs={crumbs} fallbackTo="../" />
      {showMenu ? (
        <Box style={menuStyles.layout} className="kb-topic-layout">
          <TopicMenu section={data.section} topics={data.siblings} currentId={data.id} viewHash={viewHash} />
          <div>{content}</div>
        </Box>
      ) : (
        content
      )}
    </div>
  );
};

export default ViewTopic;
